import React from 'react';
import { Filter, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Select } from '../ui/select';
import { 
  TaskArea, 
  TaskPriority, 
  TaskStatus,
  TaskAreaLabels,
  TaskPriorityLabels,
  TaskStatusLabels
} from '../../types/task';

interface TaskFiltersProps {
  filters: {
    status?: TaskStatus;
    prioridade?: TaskPriority;
    area?: TaskArea;
    unassigned?: boolean;
    sortBy?: 'newest' | 'oldest';
  };
  onFilterChange: (filters: { status?: TaskStatus; prioridade?: TaskPriority; area?: TaskArea; unassigned?: boolean; sortBy?: 'newest' | 'oldest' }) => void;
  onClearFilters: () => void;
}

export const TaskFilters: React.FC<TaskFiltersProps> = ({ 
  filters, 
  onFilterChange, 
  onClearFilters 
}) => {
  const hasActiveFilters = Object.values(filters).some(filter => filter);

  const handleFilterChange = (key: keyof typeof filters, value: string | boolean | undefined) => {
    onFilterChange({
      ...filters,
      [key]: value || undefined
    });
  };

  return (
    <Card variant="glass">
      <CardContent className="p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-foreground-secondary">
            <Filter className="w-4 h-4" />
            <span className="text-sm font-medium">Filtros:</span>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-foreground-tertiary">Status:</label>
            <div className="min-w-[120px]">
              <Select
                value={filters.status || ''}
                onChange={(value) => handleFilterChange('status', value)}
                options={[
                  { value: '', label: 'Todos' },
                  ...Object.entries(TaskStatusLabels).map(([value, label]) => ({
                    value,
                    label
                  }))
                ]}
                className="text-sm"
              />
            </div>
          </div>

          {/* Prioridade */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-foreground-tertiary">Prioridade:</label>
            <div className="min-w-[120px]">
              <Select
                value={filters.prioridade || ''}
                onChange={(value) => handleFilterChange('prioridade', value)}
                options={[
                  { value: '', label: 'Todas' },
                  ...Object.entries(TaskPriorityLabels).map(([value, label]) => ({
                    value,
                    label
                  }))
                ]}
                className="text-sm"
              />
            </div>
          </div>

          {/* Área */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-foreground-tertiary">Área:</label>
            <div className="min-w-[120px]">
              <Select
                value={filters.area || ''}
                onChange={(value) => handleFilterChange('area', value)}
                options={[
                  { value: '', label: 'Todas' },
                  ...Object.entries(TaskAreaLabels).map(([value, label]) => ({
                    value,
                    label
                  }))
                ]}
                className="text-sm"
              />
            </div>
          </div>

          {/* Ordenar por */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-foreground-tertiary">Ordenar:</label>
            <div className="min-w-[160px]">
              <Select
                value={filters.sortBy || 'newest'}
                onChange={(value) => handleFilterChange('sortBy', value)}
                options={[
                  { value: 'newest', label: 'Recentes primeiro' },
                  { value: 'oldest', label: 'Antigas primeiro' }
                ]}
                className="text-sm"
              />
            </div>
          </div>

          {/* Não vinculado */}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.unassigned === true}
                onChange={(e) => handleFilterChange('unassigned', e.target.checked ? true : undefined)}
                className="w-3 h-3 text-blue-400 bg-transparent border border-white/20 focus:ring-blue-400 focus:ring-1"
              />
              <span className="text-sm text-foreground-secondary">Não vinculado</span>
            </label>
          </div>

          {/* Limpar filtros */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="text-foreground-tertiary hover:text-foreground-primary"
            >
              <X className="w-3 h-3 mr-1" />
              Limpar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};